/**
 * InstoreQRPoster - Downloadable QR poster for in-store display
 * 
 * Uses direct canvas drawing (same approach as BrandedQRCode)
 */
import React, { useRef, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Loader2, Shield, X, Smartphone, MessageSquare, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

// Fallback SVG logo for TryOn Studio  
const TryOnStudioLogoSVG = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="44" height="44" rx="10" fill="url(#tryon-gradient)"/>
    <path d="M12 16L22 12L32 16V22C32 28 27 33 22 34C17 33 12 28 12 22V16Z" fill="white" fillOpacity="0.9"/>
    <path d="M22 18V26M18 22H26" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
    <defs>
      <linearGradient id="tryon-gradient" x1="0" y1="0" x2="44" y2="44" gradientUnits="userSpaceOnUse">
        <stop stopColor="#3B82F6"/>
        <stop offset="1" stopColor="#2563EB"/>
      </linearGradient>
    </defs>
  </svg>
);

const InstoreQRPoster = ({ 
  linkUrl, 
  businessName, 
  businessLogoUrl,
  onClose 
}) => {
  const qrContainerRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  // Download poster using direct canvas drawing (same approach as BrandedQRCode)
  const handleDownload = useCallback(async () => {
    setDownloading(true);
    
    try {
      const scale = 3; // High resolution for print
      const canvasWidth = 420 * scale;
      const canvasHeight = 800 * scale;
      
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      
      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // Helper function for rounded rect
      const roundRect = (x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
      };
      
      const padding = 32 * scale;
      
      // 1. TryOn Studio Logo (top left) - Draw as rounded rect with gradient
      const logoSize = 44 * scale;
      const logoX = padding;
      const logoY = padding;
      
      // Create gradient for logo
      const logoGradient = ctx.createLinearGradient(logoX, logoY, logoX + logoSize, logoY + logoSize);
      logoGradient.addColorStop(0, '#3B82F6');
      logoGradient.addColorStop(1, '#2563EB');
      
      ctx.fillStyle = logoGradient;
      roundRect(logoX, logoY, logoSize, logoSize, 10 * scale);
      ctx.fill();
      
      // Shield icon inside logo
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.beginPath();
      const shieldCenterX = logoX + logoSize / 2;
      const shieldCenterY = logoY + logoSize / 2;
      const shieldScale = scale * 0.8;
      ctx.moveTo(shieldCenterX - 10 * shieldScale, shieldCenterY - 6 * shieldScale);
      ctx.lineTo(shieldCenterX, shieldCenterY - 10 * shieldScale);
      ctx.lineTo(shieldCenterX + 10 * shieldScale, shieldCenterY - 6 * shieldScale);
      ctx.lineTo(shieldCenterX + 10 * shieldScale, shieldCenterY);
      ctx.quadraticCurveTo(shieldCenterX + 10 * shieldScale, shieldCenterY + 10 * shieldScale, shieldCenterX, shieldCenterY + 12 * shieldScale);
      ctx.quadraticCurveTo(shieldCenterX - 10 * shieldScale, shieldCenterY + 10 * shieldScale, shieldCenterX - 10 * shieldScale, shieldCenterY);
      ctx.closePath();
      ctx.fill();
      
      // TryOn Studio text
      ctx.fillStyle = '#1e293b';
      ctx.font = `bold ${14 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillText('TryOn Studio', logoX + logoSize + 10 * scale, logoY + 20 * scale);
      
      ctx.fillStyle = '#64748b';
      ctx.font = `${10 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillText('Virtual Try-On', logoX + logoSize + 10 * scale, logoY + 36 * scale);
      
      // 2. Business Name (centered)
      const businessY = logoY + logoSize + 40 * scale;
      
      // Business initial circle
      const initialSize = 56 * scale;
      const initialX = (canvasWidth - initialSize) / 2;
      
      ctx.fillStyle = '#eff6ff';
      roundRect(initialX, businessY, initialSize, initialSize, 12 * scale);
      ctx.fill();
      
      ctx.strokeStyle = '#dbeafe';
      ctx.lineWidth = 1 * scale;
      roundRect(initialX, businessY, initialSize, initialSize, 12 * scale);
      ctx.stroke();
      
      ctx.fillStyle = '#2563eb';
      ctx.font = `bold ${28 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(businessName?.charAt(0) || 'S', canvasWidth / 2, businessY + 40 * scale);
      
      // Business name text
      ctx.fillStyle = '#1e293b';
      ctx.font = `bold ${20 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillText(businessName || 'Store', canvasWidth / 2, businessY + initialSize + 30 * scale);
      
      ctx.fillStyle = '#2563eb';
      ctx.font = `500 ${14 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillText('Virtual Try-On Experience', canvasWidth / 2, businessY + initialSize + 52 * scale);
      
      // 3. QR Code area
      const qrAreaY = businessY + initialSize + 80 * scale;
      const qrAreaHeight = 220 * scale;
      const qrAreaPadding = 20 * scale;
      
      // QR area background
      const qrGradient = ctx.createLinearGradient(padding, qrAreaY, canvasWidth - padding, qrAreaY + qrAreaHeight);
      qrGradient.addColorStop(0, '#eff6ff');
      qrGradient.addColorStop(1, '#f8fafc');
      ctx.fillStyle = qrGradient;
      roundRect(padding, qrAreaY, canvasWidth - 2 * padding, qrAreaHeight, 16 * scale);
      ctx.fill();
      
      ctx.strokeStyle = '#dbeafe';
      ctx.lineWidth = 1 * scale;
      roundRect(padding, qrAreaY, canvasWidth - 2 * padding, qrAreaHeight, 16 * scale);
      ctx.stroke();
      
      // QR code white background
      const qrSize = 150 * scale;
      const qrX = (canvasWidth - qrSize) / 2;
      const qrY = qrAreaY + qrAreaPadding;
      
      ctx.fillStyle = '#ffffff';
      roundRect(qrX - 12 * scale, qrY, qrSize + 24 * scale, qrSize + 24 * scale, 12 * scale);
      ctx.fill();
      
      ctx.strokeStyle = '#f1f5f9';
      ctx.lineWidth = 1 * scale;
      roundRect(qrX - 12 * scale, qrY, qrSize + 24 * scale, qrSize + 24 * scale, 12 * scale);
      ctx.stroke();
      
      // Draw QR code from SVG
      const svgElement = qrContainerRef.current?.querySelector('svg');
      if (svgElement) {
        const svgClone = svgElement.cloneNode(true);
        svgClone.setAttribute('width', qrSize);
        svgClone.setAttribute('height', qrSize);
        
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const qrImg = new Image();
        await new Promise((resolve) => {
          qrImg.onload = () => {
            ctx.drawImage(qrImg, qrX, qrY + 12 * scale, qrSize, qrSize);
            URL.revokeObjectURL(svgUrl);
            resolve();
          };
          qrImg.onerror = () => {
            URL.revokeObjectURL(svgUrl);
            resolve();
          };
          qrImg.src = svgUrl;
        });
      }
      
      // "Scan to Try" text
      ctx.fillStyle = '#334155';
      ctx.font = `600 ${14 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('Scan to Try Outfits Virtually', canvasWidth / 2, qrAreaY + qrAreaHeight - 20 * scale);
      
      // 4. Steps section
      const stepsY = qrAreaY + qrAreaHeight + 24 * scale;
      const stepsHeight = 90 * scale;
      
      ctx.fillStyle = '#f8fafc';
      roundRect(padding, stepsY, canvasWidth - 2 * padding, stepsHeight, 12 * scale);
      ctx.fill();
      
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1 * scale;
      roundRect(padding, stepsY, canvasWidth - 2 * padding, stepsHeight, 12 * scale);
      ctx.stroke();
      
      // Draw 3 steps
      const stepWidth = (canvasWidth - 2 * padding) / 3;
      const steps = [
        { icon: '📱', title: 'Scan QR', subtitle: 'with your phone' },
        { icon: '💬', title: 'Verify OTP', subtitle: 'quick & secure' },
        { icon: '✨', title: 'Try On', subtitle: 'see yourself in it!' }
      ];
      
      steps.forEach((step, i) => {
        const stepX = padding + stepWidth * i + stepWidth / 2;
        const circleY = stepsY + 24 * scale;
        const circleSize = 36 * scale;
        
        // Blue circle
        ctx.fillStyle = '#2563eb';
        ctx.beginPath();
        ctx.arc(stepX, circleY, circleSize / 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Step number
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${14 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText((i + 1).toString(), stepX, circleY + 5 * scale);
        
        // Step title
        ctx.fillStyle = '#334155';
        ctx.font = `600 ${11 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
        ctx.fillText(step.title, stepX, circleY + circleSize / 2 + 16 * scale);
        
        // Step subtitle
        ctx.fillStyle = '#64748b';
        ctx.font = `${9 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
        ctx.fillText(step.subtitle, stepX, circleY + circleSize / 2 + 30 * scale);
        
        // Draw arrow between steps
        if (i < 2) {
          ctx.strokeStyle = '#cbd5e1';
          ctx.lineWidth = 2 * scale;
          ctx.beginPath();
          ctx.moveTo(stepX + stepWidth / 4, circleY);
          ctx.lineTo(stepX + stepWidth / 4 + 20 * scale, circleY);
          ctx.stroke();
        }
      });
      
      // 5. Privacy section
      const privacyY = stepsY + stepsHeight + 24 * scale;
      const privacyHeight = 80 * scale;
      
      ctx.fillStyle = '#ecfdf5';
      roundRect(padding, privacyY, canvasWidth - 2 * padding, privacyHeight, 12 * scale);
      ctx.fill();
      
      ctx.strokeStyle = '#a7f3d0';
      ctx.lineWidth = 1 * scale;
      roundRect(padding, privacyY, canvasWidth - 2 * padding, privacyHeight, 12 * scale);
      ctx.stroke();
      
      // Shield icon background
      const shieldBgSize = 36 * scale;
      const shieldBgX = padding + 16 * scale;
      const shieldBgY = privacyY + 12 * scale;
      
      ctx.fillStyle = '#d1fae5';
      roundRect(shieldBgX, shieldBgY, shieldBgSize, shieldBgSize, 8 * scale);
      ctx.fill();
      
      // Shield emoji
      ctx.font = `${18 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillText('🛡️', shieldBgX + shieldBgSize / 2, shieldBgY + 26 * scale);
      
      // Privacy title
      ctx.fillStyle = '#065f46';
      ctx.font = `600 ${11 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillText('Your Privacy is Protected', shieldBgX + shieldBgSize + 12 * scale, privacyY + 26 * scale);
      
      // Privacy text
      ctx.fillStyle = '#047857';
      ctx.font = `${9 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      const privacyText = 'Your photos remain private and visible only to you. Store cannot access your images. All data is auto-deleted after the session expires.';
      
      // Word wrap
      const maxWidth = canvasWidth - 2 * padding - shieldBgSize - 50 * scale;
      const words = privacyText.split(' ');
      let line = '';
      let lineY = privacyY + 44 * scale;
      
      for (const word of words) {
        const testLine = line + word + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && line !== '') {
          ctx.fillText(line, shieldBgX + shieldBgSize + 12 * scale, lineY);
          line = word + ' ';
          lineY += 12 * scale;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, shieldBgX + shieldBgSize + 12 * scale, lineY);
      
      // 6. Footer - Powered by
      const footerY = canvasHeight - 30 * scale;
      ctx.fillStyle = '#94a3b8';
      ctx.font = `${10 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('Powered by', canvasWidth / 2 - 30 * scale, footerY);
      
      ctx.fillStyle = '#2563eb';
      ctx.font = `600 ${10 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillText('NanoKart.ai', canvasWidth / 2 + 25 * scale, footerY);
      
      // Download the canvas
      const link = document.createElement('a');
      link.download = `${businessName?.replace(/\s+/g, '_') || 'Store'}_TryOn_QR.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('QR Poster downloaded!');
      
    } catch (err) {
      console.error('Failed to download poster:', err);
      toast.error('Failed to download. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [businessName]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-xl w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-900">Download QR Poster</h3>
            <p className="text-xs text-slate-500">Print and display in your store</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Poster Preview */}
        <div className="p-4 bg-slate-100 overflow-auto max-h-[65vh]">
          <div className="bg-white mx-auto shadow-lg rounded-xl overflow-hidden" style={{ width: '420px' }}>
            {/* Inner content with padding */}
            <div className="p-8">
              {/* Header with TryOn Studio branding */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2.5">
                  <TryOnStudioLogoSVG />
                  <div>
                    <p className="text-sm font-bold text-slate-900">TryOn Studio</p>
                    <p className="text-[10px] text-slate-500">Virtual Try-On</p>
                  </div>
                </div>
              </div>

              {/* Business Info */}
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-3 border border-blue-100">
                  <span className="text-2xl font-bold text-blue-600">
                    {businessName?.charAt(0) || 'S'}
                  </span>
                </div>
                <h1 className="text-xl font-bold text-slate-900">{businessName}</h1>
                <p className="text-sm text-blue-600 font-medium mt-1">Virtual Try-On Experience</p>
              </div>

              {/* QR Code - Hidden reference for canvas drawing */}
              <div ref={qrContainerRef} className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-6 mb-6 border border-blue-100">
                <div className="bg-white p-4 rounded-xl shadow-sm mx-auto border border-slate-100" style={{ width: 'fit-content' }}>
                  <QRCodeSVG 
                    value={linkUrl}
                    size={180}
                    level="M"
                    includeMargin={false}
                    fgColor="#1e293b"
                    bgColor="#ffffff"
                  />
                </div>
                <p className="text-center text-sm font-semibold text-slate-700 mt-4">
                  Scan to Try Outfits Virtually
                </p>
              </div>

              {/* Steps */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col items-center text-center flex-1">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mb-2 shadow-sm">
                      <Smartphone className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xs font-semibold text-slate-700">Scan QR</p>
                    <p className="text-[10px] text-slate-500">with your phone</p>
                  </div>
                  <div className="w-8 h-px bg-slate-300 mx-1" />
                  <div className="flex flex-col items-center text-center flex-1">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mb-2 shadow-sm">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xs font-semibold text-slate-700">Verify OTP</p>
                    <p className="text-[10px] text-slate-500">quick & secure</p>
                  </div>
                  <div className="w-8 h-px bg-slate-300 mx-1" />
                  <div className="flex flex-col items-center text-center flex-1">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mb-2 shadow-sm">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xs font-semibold text-slate-700">Try On</p>
                    <p className="text-[10px] text-slate-500">see yourself in it!</p>
                  </div>
                </div>
              </div>

              {/* Privacy */}
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-emerald-800 mb-1">Your Privacy is Protected</p>
                    <p className="text-[10px] text-emerald-700 leading-relaxed">
                      Your photos remain private and visible only to you. Store cannot access your images. All data is auto-deleted after the session expires.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center py-3 bg-slate-50 border-t border-slate-100">
              <p className="text-[10px] text-slate-400">
                Powered by <span className="font-semibold text-blue-600">NanoKart.ai</span>
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-slate-200 flex items-center justify-between">
          <p className="text-xs text-slate-500">Recommended: Print on A4 paper</p>
          <button
            onClick={handleDownload}
            disabled={downloading}
            data-testid="download-qr-poster-btn"
            className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 shadow-sm transition-colors"
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download Poster
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstoreQRPoster;
